import { DeviceTypes } from '@eppendorf/app-configs';
import { Icons } from '@eppendorf/vnls-css/dist';
import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import { t } from 'i18next';

import { useDeviceType } from '$components/app-config/useDeviceType';
import { useDeviceTypeConfig } from '$components/app-config/useDeviceTypeConfig';
import { TabNavigationRoute } from '$components/tabs/tabs.types';
import { searchDeviceIcon } from '$features/devices/devices.utils';

export interface DeviceViewConfig {
  deviceIcon: Icons;
  deviceDetailSidecarRoutes: TabNavigationRoute[];
  showBattery: boolean;
  showSignalStrength: boolean;
}

/**
 * Temporary solution to mock sense device types until the backend is available
 */
export function useDeviceViewConfig(device?: DeviceDTO): DeviceViewConfig {
  const deviceDetailsRoute: TabNavigationRoute = {
    label: t('deviceDetail.tabs.details'),
    path: 'details',
    icon: 'info',
  };
  const sidecarDeviceViewConfig: DeviceViewConfig = {
    deviceIcon: 'generic',
    deviceDetailSidecarRoutes: [deviceDetailsRoute],
    showBattery: false,
    showSignalStrength: false,
  };

  sidecarDeviceViewConfig.deviceDetailSidecarRoutes.push({
    label: t('deviceDetail.tabs.notes'),
    path: 'notes',
    icon: 'document', // TODO: replace with correct icon
  });

  const deviceType = useDeviceType(device) as Icons;
  const deviceTypeConfig = useDeviceTypeConfig(device);

  // fallback to generic device if no device is provided
  if (!device) return sidecarDeviceViewConfig;

  const deviceIcon = searchDeviceIcon(deviceType, undefined, device.model);

  sidecarDeviceViewConfig.deviceIcon = deviceIcon;
  sidecarDeviceViewConfig.showBattery = !!deviceTypeConfig?.operational?.isChargeable;
  sidecarDeviceViewConfig.showSignalStrength =
    !!deviceTypeConfig?.operational?.isWireless;

  switch (deviceTypeConfig?.type) {
    case DeviceTypes.MONITOR:
      sidecarDeviceViewConfig.deviceDetailSidecarRoutes.push({
        label: t('deviceDetail.tabs.associatedRouter'),
        labelShort: t('deviceDetail.tabs.associatedRouterShort'),
        path: 'associatedRouter',
        icon: DeviceTypes.GATEWAY,
      });
      break;
    case DeviceTypes.GATEWAY:
      sidecarDeviceViewConfig.deviceDetailSidecarRoutes.push({
        label: t('deviceDetail.tabs.associatedMonitors'),
        labelShort: t('deviceDetail.tabs.associatedMonitorsShort'),
        path: 'associatedMonitors',
        icon: DeviceTypes.MONITOR,
      });
      break;
    case DeviceTypes.VNBOX:
      sidecarDeviceViewConfig.deviceDetailSidecarRoutes.push({
        label: t('deviceDetail.tabs.associatedDevices'),
        labelShort: t('deviceDetail.tabs.associatedDevicesShort'),
        path: 'associatedDevices',
        icon: 'generic',
      });
      // Only add monitors tab if the vn-box 2
      if (device.type === '1008') {
        sidecarDeviceViewConfig.deviceDetailSidecarRoutes.push({
          label: t('deviceDetail.tabs.associatedMonitors'),
          labelShort: t('deviceDetail.tabs.associatedMonitorsShort'),
          path: 'associatedMonitors',
          icon: DeviceTypes.MONITOR,
        });
      }
      break;
    default:
      // Possibly VNBox-connected devices
      if ('connectedBox' in device) {
        sidecarDeviceViewConfig.deviceDetailSidecarRoutes.push({
          label: t('deviceDetail.tabs.associatedVnBox'),
          labelShort: t('deviceDetail.tabs.associatedVnBoxShort'),
          path: 'associatedBox',
          icon: 'vn-box',
        });
      }
      break;
  }

  return sidecarDeviceViewConfig;
}
